/*  ========================================================================
    # Scripts - Slider
    ========================================================================  */

/**
 * Generic slider.
 */

const Slider = {
  cfg: {
    sliders: document.querySelectorAll('.c-slider'),
    classes: {
      viewport: 'c-slider__viewport',
      shifter: 'c-slider__shifter',
      slide: 'c-slider__slide',
      slideActive: 'c-slider__slide--active',
      navLeft: 'c-slider__nav--left',
      navRight: 'c-slider__nav--right',
      navInactive: 'c-slider__nav--inactive',
      navHidden: 'c-slider__nav--hidden',
      dot: 'c-slider__dot',
      dotActive: 'c-slider__dot--active'
    },
    breakpoints: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1420
    },
    slidersArray: []
  },

  /**
   * Navigate.
   *
   * @param {Object} slider
   * @param {number} index
   */
  navigate(slider, index) {
    slider.currentIndex = index;

    // Check if the slider has slides that aren't currently visible
    if (slider.slides.count > slider.slides.visible) {
      slider.shifter.style.transform = `translateX(${-1 * slider.currentIndex * slider.slides.width}px)`;
    }

    this.addClasses(slider);
  },

  /**
   * Navigate - Left
   *
   * @param {Object} slider
   */
  navigateLeft(slider) {
    let newIndex = slider.currentIndex;

    if (newIndex === 0) {
      newIndex = slider.endless === true ? slider.slides.count - slider.slides.visible : slider.currentIndex;
    } else {
      newIndex = slider.currentIndex - 1;
    }

    this.navigate(slider, newIndex);
  },

  /**
   * Navigate - Right
   *
   * @param {Object} slider
   */
  navigateRight(slider) {
    let newIndex = slider.currentIndex;

    if (newIndex + slider.slides.visible >= slider.slides.count) {
      newIndex = slider.endless === true ? 0 : slider.currentIndex;
    } else {
      newIndex = slider.currentIndex + 1;
    }

    this.navigate(slider, newIndex);
  },

  /**
   * Touch Support.
   *
   * @param {Object} slider
   */
  touchSupport(slider) {
    let coordinatesStart = 0;
    let coordinatesEnd = 0;

    slider.viewport.addEventListener(
      'touchstart',
      event => {
        coordinatesStart = event.changedTouches[0].screenX;
      },
      {
        capture: true,
        passive: true
      }
    );

    slider.viewport.addEventListener(
      'touchend',
      event => {
        coordinatesEnd = event.changedTouches[0].screenX;

        if (coordinatesEnd <= coordinatesStart - 40) {
          // Swipe to the left
          this.navigateRight(slider);
        } else if (coordinatesEnd >= coordinatesStart + 40) {
          // Swipe to the right
          this.navigateLeft(slider);
        }
      },
      {
        capture: true,
        passive: true
      }
    );
  },

  /**
   * Add Handlers.
   *
   * @param {Object} slider
   */
  addHandlers(slider) {
    // Arrow - Left
    if (typeof slider.ui.navLeft !== undefined && slider.ui.navLeft !== null) {
      slider.ui.navLeft.addEventListener('click', () => {
        this.navigateLeft(slider);
      });
    }

    // Arrow - Right
    if (typeof slider.ui.navRight !== undefined && slider.ui.navRight !== null) {
      slider.ui.navRight.addEventListener('click', () => {
        this.navigateRight(slider);
      });
    }

    // Dots
    if (typeof slider.ui.dots !== undefined && slider.ui.dots !== null) {
      slider.ui.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.navigate(slider, index);
        });
      });
    }

    // Touch
    this.touchSupport(slider);
  },

  /**
   * Add Classes.
   *
   * @param {Object} slider
   */
  addClasses(slider) {
    // Slides
    slider.slides.items.forEach((slide, index) => {
      slide.classList.remove(this.cfg.classes.slideActive);

      if (index === slider.currentIndex) {
        slide.classList.add(this.cfg.classes.slideActive);
      }
    });

    // Arrow - Left
    if (typeof slider.ui.navLeft !== undefined && slider.ui.navLeft !== null) {
      if (slider.currentIndex === 0) {
        slider.ui.navLeft.classList.add(this.cfg.classes.navInactive);
      } else {
        slider.ui.navLeft.classList.remove(this.cfg.classes.navInactive);
      }
    }

    // Arrow - Right
    if (typeof slider.ui.navRight !== undefined && slider.ui.navRight !== null) {
      if (slider.currentIndex + slider.slides.visible >= slider.slides.count) {
        slider.ui.navRight.classList.add(this.cfg.classes.navInactive);
      } else {
        slider.ui.navRight.classList.remove(this.cfg.classes.navInactive);
      }
    }

    // Arrows - Left & Right
    if (
      typeof slider.ui.navLeft !== undefined &&
      slider.ui.navLeft !== null &&
      typeof slider.ui.navRight !== undefined &&
      slider.ui.navRight !== null
    ) {
      if (
        slider.ui.navLeft.classList.contains(this.cfg.classes.navInactive) &&
        slider.ui.navRight.classList.contains(this.cfg.classes.navInactive)
      ) {
        slider.ui.navLeft.classList.add(this.cfg.classes.navHidden);
        slider.ui.navRight.classList.add(this.cfg.classes.navHidden);
      } else {
        slider.ui.navLeft.classList.remove(this.cfg.classes.navHidden);
        slider.ui.navRight.classList.remove(this.cfg.classes.navHidden);
      }
    }

    // Dots
    if (typeof slider.ui.dots !== undefined && slider.ui.dots !== null) {
      slider.ui.dots.forEach((dot, index) => {
        dot.classList.remove(this.cfg.classes.dotActive);

        if (index === slider.currentIndex) {
          dot.classList.add(this.cfg.classes.dotActive);
        }
      });
    }
  },

  /**
   * Autoslide
   *
   * @param {Object} slider
   */
  autoslide(slider) {
    if (
      slider.endless === true &&
      slider.autoslide.active === true &&
      slider.autoslide.interval >= 500
    ) {
      setInterval(() => {
        this.navigateRight(slider);
      }, slider.autoslide.interval);
    }
  },

  /**
   * Get current breakpoint.
   */
  getCurrentBreakpoint() {
    const width = window.innerWidth;
    let breakpoint;

    if (width >= this.cfg.breakpoints.xs) {
      breakpoint = 'xs';
    }
    if (width >= this.cfg.breakpoints.sm) {
      breakpoint = 'sm';
    }
    if (width >= this.cfg.breakpoints.md) {
      breakpoint = 'md';
    }
    if (width >= this.cfg.breakpoints.lg) {
      breakpoint = 'lg';
    }
    if (width >= this.cfg.breakpoints.xl) {
      breakpoint = 'xl';
    }
    if (width >= this.cfg.breakpoints.xxl) {
      breakpoint = 'xxl';
    }

    return breakpoint;
  },

  /**
   * Resize.
   */
  resize() {
    this.cfg.slidersArray.forEach(slider => {
      slider.slides.visible = slider.visibleSlides[this.getCurrentBreakpoint()];
      slider.slides.width = slider.viewport.offsetWidth / slider.slides.visible;

      // Add the correct width to each slide
      slider.slides.items.forEach(entry => {
        entry.style.width = `${slider.slides.width}px`;
      });

      // Add the correct index to each slider
      if (slider.currentIndex + slider.slides.visible > slider.slides.count) {
        slider.currentIndex = slider.slides.count - slider.slides.visible;
        if (slider.currentIndex < 0) {
          slider.currentIndex = 0;
        }
      }
      this.navigate(slider, slider.currentIndex);
    });
  },

  /**
   * Creates a slider object for each slider on the current page.
   */
  sliders() {
    this.cfg.sliders.forEach(slider => {
      const sliderObject = {
        slider,
        viewport: slider.querySelector(`.${this.cfg.classes.viewport}`),
        shifter: slider.querySelector(`.${this.cfg.classes.shifter}`),
        slides: {
          items: slider.querySelectorAll(`.${this.cfg.classes.slide}`),
          count: slider.querySelectorAll(`.${this.cfg.classes.slide}`).length,
          visible: 0,
          width: 0
        },
        ui: {
          navLeft: slider.querySelector(`.${this.cfg.classes.navLeft}`),
          navRight: slider.querySelector(`.${this.cfg.classes.navRight}`),
          dots: slider.querySelectorAll(`.${this.cfg.classes.dot}`)
        },
        visibleSlides: {
          xs: parseInt(slider.dataset.slidesXs, 10),
          sm: parseInt(slider.dataset.slidesSm, 10),
          md: parseInt(slider.dataset.slidesMd, 10),
          lg: parseInt(slider.dataset.slidesLg, 10),
          xl: parseInt(slider.dataset.slidesXl, 10),
          xxl: parseInt(slider.dataset.slidesXxl, 10)
        },
        endless: JSON.parse(slider.dataset.slidesEndless.toLowerCase()) || false,
        autoslide: {
          active: JSON.parse(slider.dataset.slidesAuto.toLowerCase()) || false,
          interval: parseInt(slider.dataset.slidesAutoInterval, 10) || 0
        },
        currentIndex: 0
      };

      this.addHandlers(sliderObject);
      this.autoslide(sliderObject);

      this.cfg.slidersArray.push(sliderObject);
    });
  },

  /**
   * Initialize.
   */
  init() {
    if (typeof this.cfg.sliders !== undefined && this.cfg.sliders !== null) {
      this.sliders();
      this.resize();

      window.addEventListener('resize', () => {
        this.resize();
      });
    }
  }
};

export default Slider;
